/**
 * The Design tab — theme SELECTION, and a preview that is the real thing.
 *
 * Visual Reference: `design/screens/editor-design.html` — default · loading ·
 * error. No empty state, because a theme is always selected.
 *
 * PRD §7.5: "Design | Theme selection; **token editor deferred to M6**." So
 * there are four cards and no colour pickers, and the deferral line renders
 * verbatim rather than being implied by their absence.
 *
 * The swatch palettes are CONTENT — `design/DESIGN-SYSTEM.md` §4.7 — and live
 * in `editor/themes.ts` with the token-gate exception that records why.
 *
 * THE `Powered by Leadgible` TOGGLE IS HERE, and it took a design pass to get
 * here. DECISION-057 put it on this tab and refused to let the story invent it;
 * Q-047 recorded that the story could not finish without a drawing; DECISION-070
 * ran the pass. `editor-design.html` now draws all three plan states, and this
 * renders the ONE that matches the org — the other two rows are annotation,
 * which the mockup's own caption says in as many words ("the other two show how
 * the row reads further down the plan ladder").
 *
 * Story 6.5.
 */
import { useCallback, useEffect, useState } from 'react'
import type { Funnel } from '@leadgible/schema'
import {
  INHERIT_CARD,
  LAYOUTS,
  M6_DEFERRAL,
  PROGRESS,
  THEMES,
  progressApplies,
  type ThemePreset,
} from './themes'
import { Preview, PREVIEW_WIDTHS, type PreviewWidth } from './Preview'
import { edit, useEditor } from './store'
import { ApiFailure, apiGet, apiPut } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Callout } from '../ui/Callout'
import { LockMark } from '../ui/LockMark'
import { Skeleton } from '../ui/Skeleton'

/** Where a preview submission would go — see `Preview.tsx`. */
const previewOrigin = () => window.location.origin

export function DesignTab() {
  const { workspace, me, org } = useShell()
  const workspaceId = workspace?.id ?? null
  /*
    Story 26.5 — the preview carries the AI disclosure exactly when a respondent's
    funnel will: the server shows it while the deployment has an AI layer and this
    organisation's Owner has not switched it off, and `aiByOrg` is that same
    answer on the boot payload (Story 23.5). Absent reads as available, as the
    Qualify tab reads it.
  */
  const ai = (org ? me.aiByOrg?.[org.id] : undefined) ?? me.ai
  const aiDisclosed = ai ? ai.available : true
  const funnel = useEditor((s) => s.funnel)
  const [width, setWidth] = useState<PreviewWidth>(375)
  if (!funnel) return null

  const theme = funnel.theme_ref
  const layout = funnel.settings.layout_mode
  const progress = funnel.settings.progress_style

  return (
    <div className="flex min-h-0 flex-col gap-mdx-24 px-mdx-24 pb-mdx-48 pt-mdx-28 xl:flex-row xl:items-start">
      <div className="flex min-w-0 flex-1 flex-col gap-mdx-24">
        <Card
          title="Theme"
          aside="How the funnel looks on your site"
        >
          <div className="grid grid-cols-1 gap-mdx-12 sm:grid-cols-2">
            <ThemeCard
              preset={{ ...INHERIT_CARD, id: 'inherit', name: INHERIT_CARD.name, what: INHERIT_CARD.what }}
              selected={theme.kind === 'inherit'}
              badge={theme.kind === 'inherit' ? `Inheriting from ${hostOf(funnel)}` : undefined}
              onSelect={() =>
                edit('Match my website', (draft) => {
                  draft.theme_ref = { kind: 'inherit' }
                })
              }
            />
            {THEMES.map((preset) => (
              <ThemeCard
                key={preset.id}
                preset={preset}
                selected={theme.kind === 'preset' && theme.preset_id === preset.id}
                onSelect={() =>
                  edit(`Use the ${preset.name} theme`, (draft) => {
                    draft.theme_ref = { kind: 'preset', preset_id: preset.id }
                  })
                }
              />
            ))}
          </div>
          <p className="m-0 text-mdx-13 text-mdx-slate">{M6_DEFERRAL}</p>
        </Card>

        <Card title="Layout" aside="How much a respondent sees at once">
          <div className="flex flex-col gap-mdx-8">
            {LAYOUTS.map((mode) => (
              <Choice
                key={mode.id}
                name={mode.name}
                what={mode.what}
                badge={mode.recommended ? 'Recommended' : undefined}
                selected={layout === mode.id}
                onSelect={() =>
                  edit(`Use the ${mode.name} layout`, (draft) => {
                    draft.settings.layout_mode = mode.id
                  })
                }
              />
            ))}
          </div>
        </Card>

        <Card title="Progress" aside="Wizard and Grouped only">
          <div className="flex flex-wrap gap-mdx-8">
            {PROGRESS.map((style) => (
              <Segment
                key={style.id}
                name={style.name}
                selected={progress === style.id}
                // The export states the constraint on the card; this makes it
                // true rather than advisory. Single page shows everything at
                // once and Conversational has no discrete screens, so neither
                // has a position to report.
                disabled={!progressApplies(layout)}
                onSelect={() =>
                  edit(`Show progress as ${style.name}`, (draft) => {
                    draft.settings.progress_style = style.id
                  })
                }
              />
            ))}
          </div>
          {progressApplies(layout) ? null : (
            <p className="m-0 text-mdx-13 text-mdx-slate">
              {LAYOUTS.find((l) => l.id === layout)?.name} has no steps to count, so there is no
              progress to show.
            </p>
          )}
        </Card>

        <Branding />
      </div>

      <section className="flex min-w-0 flex-col gap-mdx-12 xl:w-mdx-header-column">
        <div className="flex flex-wrap items-baseline justify-between gap-mdx-8">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">Live preview</span>
          <span className="flex gap-mdx-6">
            {PREVIEW_WIDTHS.map((candidate) => (
              <Segment
                key={candidate}
                name={`${candidate}px`}
                selected={width === candidate}
                onSelect={() => setWidth(candidate)}
              />
            ))}
          </span>
        </div>
        {/*
          Scrolls rather than being capped: the 1440 preview is wider than this
          column at every dashboard width, and a capped height would be an
          invented dimension with no token behind it.
        */}
        <div className="flex justify-center overflow-auto rounded-mdx-card border border-mdx-line bg-mdx-ground p-mdx-12">
          <Preview
            funnel={funnel}
            width={width}
            apiOrigin={previewOrigin()}
            workspaceId={workspaceId}
            aiDisclosed={aiDisclosed}
            disclosureText={org?.disclosureText ?? null}
          />
        </div>
        {/*
          DECISION-071 changed what this sentence can honestly say. Submit now
          reaches the answer probe, so the operator gets a REAL scored outcome —
          the same engine a respondent's answers go through — and no lead is
          created, no meter consumed and nothing dispatched.
        */}
        <p className="m-0 max-w-mdx-measure-56 text-mdx-13 text-mdx-slate">
          The real embed runtime, running your draft. Submitting scores your answers for real and
          shows the outcome a respondent would see — no lead is created and nothing is saved.
        </p>
      </section>
    </div>
  )
}

/** The host the funnel says it is embedded on, for the inherit badge. */
function hostOf(funnel: Funnel): string {
  const first = (funnel as { domain_allowlist?: string[] }).domain_allowlist?.[0]
  return first ?? 'your site'
}

/* ── the export's own controls ──────────────────────────────────────────── */

function Card({
  title,
  aside,
  children,
}: {
  title: string
  /** Optional: the export gives Branding a bare heading and no qualifier. */
  aside?: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
        <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</span>
        {aside ? <span className="text-mdx-13 text-mdx-slate">{aside}</span> : null}
      </div>
      {children}
    </section>
  )
}

/* ── the badge ──────────────────────────────────────────────────────────── */

/** The export's copy, verbatim. Both sentences are promises about the product. */
const BADGE_TITLE = 'Powered by Leadgible'
const BADGE_WHAT = 'Shown in the corner of every step.'
const BADGE_LOCKED = 'Removing the badge is on Starter and above.'

type BrandingView = { badge: boolean; override: boolean | null; removable: boolean }

/**
 * `Powered by Leadgible` — one row, in whichever of its two states this org is in.
 *
 * ORG-SCOPED ON A WORKSPACE-SCOPED SCREEN, which is why it fetches rather than
 * reading the funnel. The badge is PRD §4.5's, sold per plan and stored on
 * `org_settings` — every funnel in the organisation wears the same answer, and
 * a per-funnel control would be selling something the plan ladder does not have.
 *
 * TWO STATES, NOT THREE. `branding_removable` decides which: a plan that may
 * remove the badge gets the live toggle, and one that may not gets the locked
 * row that says why — DECISION-057's "a control that explains why it cannot be
 * used, not one that refuses". The server refuses too (`route-contract.ts`
 * gates the PUT on the same flag), so this is the explanation, not the fence.
 */
function Branding() {
  const { org } = useShell()
  const orgId = org?.id ?? null

  const [view, setView] = useState<BrandingView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!orgId) return
    setError(null)
    try {
      setView(await apiGet<BrandingView>(`/v1/orgs/${orgId}/branding`))
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't load your branding setting. Check your connection and try again.",
      )
    }
  }, [orgId])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * OPTIMISTIC, THEN CORRECTED BY THE SERVER'S OWN ANSWER.
   *
   * The switch moves on click because a toggle that waits for a round trip
   * reads as broken. What comes back is the RESOLVED state — plan default,
   * overridden — so a refusal or a disagreement lands as the truth rather than
   * as whatever was clicked. On failure the row goes back to where it was and
   * says why, because RULE-078 forbids a failure that needs somebody awake to
   * notice.
   */
  const toggle = async (next: boolean) => {
    if (!orgId || !view || saving) return
    const previous = view
    setView({ ...view, badge: next, override: next })
    setSaving(true)
    setError(null)
    try {
      setView(await apiPut<BrandingView>(`/v1/orgs/${orgId}/branding`, { badge: next }))
    } catch (e) {
      setView(previous)
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't save that. Check your connection and try again.",
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card title="Branding">
      {error ? <Callout>{error}</Callout> : null}
      {!view ? (
        /*
          The real row with blocks in its slots, not a rectangle standing in for
          it — `DESIGN-SYSTEM.md` §2: "preserve the real layout so nothing jumps
          when content lands." It also means the row's height stays a
          consequence of its padding and type rather than a number this file
          would have had to invent a token for.
        */
        <div className="flex items-center gap-mdx-16 rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-14">
          <span className="flex min-w-0 flex-1 flex-col gap-mdx-6">
            <Skeleton className="h-mdx-14" style={{ width: '42%' }} />
            <Skeleton className="h-mdx-12" style={{ width: '64%' }} />
          </span>
          <Skeleton className="h-mdx-24 w-mdx-40 flex-none rounded-mdx-pill" />
        </div>
      ) : (
        <div className="flex items-center gap-mdx-16 rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-14">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-mdx-15 font-mdx-semibold">{BADGE_TITLE}</span>
            <span className="text-mdx-13 text-mdx-slate">
              {view.removable ? (
                BADGE_WHAT
              ) : (
                <>
                  {BADGE_LOCKED}{' '}
                  <a
                    href="/billing"
                    className="whitespace-nowrap font-mdx-semibold text-mdx-brand-deep no-underline"
                  >
                    See plans
                  </a>
                </>
              )}
            </span>
          </span>

          {view.removable ? (
            <button
              type="button"
              role="switch"
              aria-checked={view.badge}
              aria-label={BADGE_TITLE}
              disabled={saving}
              onClick={() => void toggle(!view.badge)}
              className={[
                'flex h-mdx-24 w-mdx-40 flex-none cursor-pointer items-center rounded-mdx-pill',
                'border-0 p-mdx-3 transition-colors ease-mdx-swift disabled:cursor-wait',
                view.badge ? 'justify-end bg-mdx-brand' : 'justify-start bg-mdx-line',
              ].join(' ')}
            >
              <span className="size-mdx-18 rounded-mdx-pill bg-mdx-white" />
            </button>
          ) : (
            /*
              A `span`, not a disabled `button`. There is nothing to press: the
              plan cannot remove the badge, the sentence beside it says so, and
              the way forward is the "See plans" link rather than a control that
              takes a click and refuses it.
            */
            <span className="flex flex-none items-center gap-mdx-8 text-mdx-slate">
              <LockMark />
              <span
                role="switch"
                aria-checked="true"
                aria-disabled="true"
                aria-label={BADGE_TITLE}
                className="flex h-mdx-24 w-mdx-40 flex-none items-center justify-end rounded-mdx-pill border border-mdx-line bg-mdx-nurture-tint p-mdx-2 opacity-mdx-withheld"
              >
                <span className="size-mdx-18 rounded-mdx-pill border border-mdx-line bg-mdx-white" />
              </span>
            </span>
          )}
        </div>
      )}
    </Card>
  )
}

/**
 * One theme card, drawing the theme's OWN palette.
 *
 * Every colour on the miniature is an inline style, and that is correct here
 * and nowhere else in this package: §4.7 makes these preset content rather than
 * Leadgible tokens. A Tailwind class could not express them anyway — they are
 * data, and Story 7.5 turns them into rows.
 */
function ThemeCard({
  preset,
  selected,
  badge,
  onSelect,
}: {
  preset: ThemePreset
  selected: boolean
  badge?: string
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={[
        'flex cursor-pointer flex-col gap-mdx-12 rounded-mdx-card bg-mdx-white p-mdx-14 text-left',
        selected ? 'border-2 border-mdx-brand' : 'border border-mdx-line',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className="relative block h-mdx-textarea-min overflow-hidden rounded-mdx-control p-mdx-12"
        style={{ background: preset.ground, border: `1px solid ${preset.edge}` }}
      >
        <span
          className="block h-mdx-9"
          style={{ width: '52%', borderRadius: preset.barRadius, background: preset.heading }}
        />
        <span
          className="mt-mdx-7 block h-mdx-6"
          style={{ width: '76%', borderRadius: preset.barRadius, background: preset.muted }}
        />
        <span
          className="mt-mdx-14 block h-mdx-evidence-icon"
          style={{ width: '39%', borderRadius: preset.buttonRadius, background: preset.accent }}
        />
        <span className="absolute bottom-mdx-10 right-mdx-10 flex gap-mdx-4">
          {preset.dots.map((dot, index) => (
            <span
              key={index}
              className="size-mdx-12 rounded-mdx-pill"
              style={{ background: dot }}
            />
          ))}
        </span>
      </span>

      <span className="flex items-center gap-mdx-8">
        <span className="min-w-0 flex-1 text-mdx-15 font-mdx-semibold">{preset.name}</span>
        {selected ? (
          <span
            aria-hidden="true"
            className="flex size-mdx-22 flex-none items-center justify-center rounded-mdx-pill bg-mdx-brand text-mdx-12 font-mdx-bold text-mdx-white"
          >
            ✓
          </span>
        ) : null}
      </span>
      <span className="text-mdx-13 text-mdx-slate">{preset.what}</span>
      {badge ? (
        <span className="self-start rounded-mdx-pill bg-mdx-brand-tint px-mdx-12 py-mdx-5 text-mdx-13 font-mdx-semibold text-mdx-brand-deep">
          {badge}
        </span>
      ) : null}
    </button>
  )
}

function Choice({
  name,
  what,
  badge,
  selected,
  onSelect,
}: {
  name: string
  what: string
  badge?: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={[
        'flex cursor-pointer items-center gap-mdx-16 rounded-mdx-control bg-mdx-white px-mdx-14 py-mdx-12 text-left',
        selected ? 'border-2 border-mdx-brand' : 'border border-mdx-line',
      ].join(' ')}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-mdx-2">
        <span className="flex flex-wrap items-center gap-mdx-8">
          <span className="text-mdx-15 font-mdx-semibold">{name}</span>
          {badge ? (
            <span className="rounded-mdx-pill bg-mdx-hot-tint px-mdx-9 py-mdx-3 text-mdx-12 font-mdx-semibold text-mdx-hot-ink">
              {badge}
            </span>
          ) : null}
        </span>
        <span className="text-mdx-13 text-mdx-slate">{what}</span>
      </span>
    </button>
  )
}

function Segment({
  name,
  selected,
  disabled = false,
  onSelect,
}: {
  name: string
  selected: boolean
  disabled?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onSelect}
      className={[
        'rounded-mdx-control-sm px-mdx-16 py-mdx-8 text-mdx-13 font-mdx-semibold',
        'disabled:cursor-not-allowed disabled:opacity-mdx-recessed',
        disabled ? '' : 'cursor-pointer',
        selected
          ? 'border border-mdx-brand bg-mdx-brand-tint text-mdx-brand-deep'
          : 'border border-mdx-line bg-mdx-white text-mdx-ink',
      ].join(' ')}
    >
      {name}
    </button>
  )
}
